import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { CalculateLinealFeetResp, CalculateLinealFeetRqst, InspectionsApiService, Package } from '@xpo-ltl/sdk-inspections';
import { take} from 'rxjs';


@Component({
  selector: 'app-linealFeet',
  templateUrl: './linealFeet.component.html',
  styleUrls: ['./linealFeet.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LinealFeetComponent {
  initialNumberOfCommodities = 3;
  isCalculated: boolean = false;
  totals;
  errorMessage: string;

  @ViewChild(MatTooltip) tooltip: MatTooltip;

  form = this.fb.group({
    rowsToAdd: 1,
    commodities: this.fb.array([])
  });

  constructor(
    private inspectionsApiService: InspectionsApiService,
    private fb:FormBuilder
  ) {
    this.populateCommodities();
  }

  get commodities() {
    return this.form.controls["commodities"] as FormArray;
  }

  showTooltip() {
    this.tooltip.show();
  }

  addCommodity(pieceCnt = '', weight = '', length = '', width = '', height = '', volume = '', area = '', density = '') {
    const packageForm = this.fb.group({
        pieceCnt: [pieceCnt],
        weight: [weight],
        length: [length],
        width: [width],
        height: [height],
        volume: [{value: volume, disabled: true}],
        area: [{value: area, disabled: true}],
        density: [{value: density, disabled: true}],
    }) as FormGroup;
    this.commodities.push(packageForm);
  }

  public checkInputValues(i){
    const commodities = this.form.controls["commodities"] as FormArray;
    const commodityItem = commodities.controls[i];
    const controls = commodityItem["controls"];

    for (const key in controls) {
      const item = controls[key];
      item.setErrors(null);
    }
    for (const key in controls) {
      if (this.areFormValuesEntered(controls) && controls.hasOwnProperty(key)) {
        const item = controls[key];
        if (key !== "weight" && (item.value === "" || item.value === "0")){
          if (key !== "volume" && key !== "area" && key !== "density") {
            item.setErrors({ required: true });
          }
        }
      }
    }
    this.checkWeight();
  }

  checkWeight(){
    let hasWeight = false;
    const commodities = this.form.controls["commodities"] as FormArray;
    commodities.controls.forEach((commodityItem) => {
      const controls = commodityItem["controls"];
      for (const key in controls) {
        if (this.areFormValuesEntered(controls) && controls.hasOwnProperty(key)) {
          const item = controls[key];
          if (key === "weight" && item.value !== "" &&  item.value !== "0"){
            hasWeight = true;
          }
        }
      }
    })
    if (!hasWeight){
      this.setErrorOnWeight();
    } else {
      this.clearWeightErrors();
    }
  }

  clearWeightErrors() {
    const commodities = this.form.controls["commodities"] as FormArray;
    commodities.controls.forEach((commodityItem) => {
      const controls = commodityItem["controls"];
      for (const key in controls) {
        if (this.areFormValuesEntered(controls) && controls.hasOwnProperty(key)) {
          const item = controls[key];
          if (key === "weight" ){
            item.setErrors(null);
          }
        }
      }
    })
  }

  setErrorOnWeight() {
    const commodities = this.form.controls["commodities"] as FormArray;
    commodities.controls.forEach((commodityItem) => {
      const controls = commodityItem["controls"];
      for (const key in controls) {
        if (this.areFormValuesEntered(controls) && controls.hasOwnProperty(key)) {
          const item = controls[key];
          if (key === "weight" ){
            item.setErrors({ required: true });
          }
        }
      }
    })
  }

  isFormValid():boolean{
    const commodities = this.form.controls["commodities"] as FormArray;
    let atLeastOneEntered = false;
    let hasError = false;
    commodities.controls.forEach((commodityItem) => {
      const controls = commodityItem["controls"];
      if (this.areFormValuesEntered(controls)){
        atLeastOneEntered = true;
      }
      if (this.hasError(controls)){
        hasError = true;
      }
    })
    return atLeastOneEntered && !hasError;
  }

  areFormValuesEntered(controls):boolean{
    let needsChecking = false;
    for (const key in controls ) {
      if (controls.hasOwnProperty(key)) {
        const item = controls[key];
        if (item.value !== ""){
          needsChecking = true;
        }
      }
    }
    return needsChecking;
  }

  hasError(controls):boolean{
    let hasErrors = false;
    for (const key in controls ) {
      if (controls.hasOwnProperty(key)) {
        const item = controls[key];
        if (item.errors !== null){
          hasErrors = true;
        }
      }
    }
    return hasErrors;
  }

  populateCommodities() {
    for (let i = 0; i < this.initialNumberOfCommodities; i++) {
      this.addCommodity();
    }
  }

  addRowsClick(){
    const numberToAdd = Number(this.form.controls["rowsToAdd"].value);
    for (let i = 0; i < numberToAdd; i++) {
      this.addCommodity();
    }
  }

  resetClick(){
    this.form.controls["commodities"] = this.fb.array([]);
    this.populateCommodities();
    this.isCalculated = false;
    this.errorMessage = undefined;
  }

  calculateClick(){
    let packages: Package[] = this.getPackagesToSubmit();
    const request: CalculateLinealFeetRqst = new CalculateLinealFeetRqst();
    request.packages = packages;
    this.inspectionsApiService.calculateLinealFeet(request).pipe(take(1)).subscribe(
      (results: CalculateLinealFeetResp) => {
      this.isCalculated = true;
      this.totals = results;
      this.form.controls["commodities"] = this.fb.array([]);
      this.addPopulatedPackages(results.packages);
      this.errorMessage = undefined;
    }, (error) => {
      this.errorMessage = error.error.moreInfo[0].message;
    });
  }

  addPopulatedPackages(packages: Package[]){
    packages.forEach((packageItem: Package) => {
      this.addCommodity(
        packageItem.pieceCnt.toString(), 
        packageItem.weight.toString(),
        packageItem.length.toString(),
        packageItem.width.toString(),
        packageItem.height.toString(),
        packageItem.volume.toString(),
        packageItem.area.toString(),
        packageItem.density.toString())
    })
  }

  getPackagesToSubmit(): Package[] {
    let packages: Package[] = [];
    const commodities = this.form.controls["commodities"] as FormArray;
    commodities.controls.forEach((commodityItem) => {
      const controls = commodityItem["controls"];
      let needsChecking = this.areFormValuesEntered(controls);
      let commodityPackage: Package = new Package();
      for (const key in controls) {
        if (needsChecking && controls.hasOwnProperty(key)) {
          const item = controls[key];
          let value = item.value;
          if (key === "weight" && value === ''){
            value = '0';
          }
          commodityPackage[key] = value;
        }
      }
      if (needsChecking){
        packages.push(commodityPackage);
      }
    })
    return packages;
  }
}
